(() => {
  const reducedMotion = window.matchMedia('(min-width: 999999px)');
  const nativeFetch = window.fetch.bind(window);
  let pendingFlight = null;
  let activeTrigger = null;
  let activeProduct = null;
  let activeCard = null;
  let selectedOptions = [];
  const productCache = new Map();
  let recentlyViewedSequence = 0;
  let recentlyViewedAbort = null;
  const checkoutAttentionTimers = new WeakMap();

  const clearCheckoutAttention = (drawer) => {
    const timer = checkoutAttentionTimers.get(drawer);
    if (timer) window.clearTimeout(timer);
    checkoutAttentionTimers.delete(drawer);
    drawer
      ?.querySelector('#CartDrawer-Checkout.is-cc-checkout-attention')
      ?.classList.remove('is-cc-checkout-attention');
  };

  const scheduleCheckoutAttention = (drawer) => {
    clearCheckoutAttention(drawer);
    if (!drawer?.classList.contains('active') || reducedMotion.matches) return;

    const timer = window.setTimeout(() => {
      checkoutAttentionTimers.delete(drawer);
      if (!drawer.classList.contains('active')) return;

      const checkout = drawer.querySelector('#CartDrawer-Checkout');
      const shine = checkout?.querySelector('.cc-checkout-shine');
      if (!checkout || !shine || checkout.disabled || !checkout.getClientRects().length) return;

      checkout.classList.remove('is-cc-checkout-attention');
      void checkout.offsetWidth;
      checkout.classList.add('is-cc-checkout-attention');

      const cleanup = () => checkout.classList.remove('is-cc-checkout-attention');
      shine.addEventListener('animationend', cleanup, { once: true });
      window.setTimeout(cleanup, 1200);
    }, 5000);

    checkoutAttentionTimers.set(drawer, timer);
  };

  const bindCheckoutAttention = (scope = document) => {
    const drawers = new Set(scope.querySelectorAll?.('cart-drawer') || []);
    if (scope.matches?.('cart-drawer')) drawers.add(scope);
    const parentDrawer = scope.closest?.('cart-drawer');
    if (parentDrawer) drawers.add(parentDrawer);

    drawers.forEach((drawer) => {
      if (drawer.dataset.ccCheckoutAttentionBound === 'true') return;
      drawer.dataset.ccCheckoutAttentionBound = 'true';
      let wasOpen = drawer.classList.contains('active');

      new MutationObserver(() => {
        const isOpen = drawer.classList.contains('active');
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        if (isOpen) scheduleCheckoutAttention(drawer);
        else clearCheckoutAttention(drawer);
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

      if (wasOpen) scheduleCheckoutAttention(drawer);
    });
  };

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'NZD';
    const locale = document.documentElement.lang || 'en-NZ';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(Number(cents || 0) / 100);
  };

  const activeCurrencyRate = () => {
    const directRate = Number(window.Shopify?.currency?.rate);
    if (Number.isFinite(directRate) && directRate > 0) return directRate;

    const currencyScript = [...document.scripts].find((script) =>
      script.textContent?.includes('Shopify.currency =')
    );
    const currencyMatch = currencyScript?.textContent?.match(/Shopify\.currency\s*=\s*([^;]+);/);
    if (!currencyMatch) return 1;

    try {
      const parsed = JSON.parse(currencyMatch[1]);
      const parsedRate = Number(parsed.rate);
      return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : 1;
    } catch (_error) {
      return 1;
    }
  };

  const updateShippingProgress = (scope = document) => {
    const sections = new Set(scope.querySelectorAll?.('[data-cc-shipping-threshold-usd]') || []);
    if (scope.matches?.('[data-cc-shipping-threshold-usd]')) sections.add(scope);
    const parentSection = scope.closest?.('[data-cc-shipping-threshold-usd]');
    if (parentSection) sections.add(parentSection);

    sections.forEach((section) => {
      const thresholdUsd = Number(section.dataset.ccShippingThresholdUsd);
      const cartTotalCents = Number(section.dataset.ccShippingCartTotal);
      const currency = section.dataset.ccShippingCurrency || window.Shopify?.currency?.active || 'USD';
      const rate = currency === 'USD' ? 1 : activeCurrencyRate();
      if (!Number.isFinite(thresholdUsd) || !Number.isFinite(cartTotalCents) || rate <= 0) return;

      const thresholdCents = window.ccMoney.thresholdCents(thresholdUsd, currency, rate);
      const remainingCents = Math.max(0, thresholdCents - cartTotalCents);
      const progress = Math.min(100, Math.max(0, (cartTotalCents / thresholdCents) * 100));
      const progressBar = section.querySelector('.cc-cart-shipping__progress');
      const message = section.querySelector('[data-cc-shipping-message]');
      if (!progressBar || !message) return;

      progressBar.style.setProperty('--cc-shipping-progress', `${progress.toFixed(2)}%`);
      progressBar.setAttribute('aria-valuemin', '0');
      progressBar.setAttribute('aria-valuemax', String(thresholdCents / 100));
      progressBar.setAttribute('aria-valuenow', String(Math.min(cartTotalCents, thresholdCents) / 100));

      const status = remainingCents === 0
        ? 'Free standard shipping is unlocked.'
        : `${money(remainingCents)} away from free standard shipping.`;
      message.textContent = status;
      progressBar.setAttribute('aria-valuetext', status);
      section.classList.toggle('is-unlocked', remainingCents === 0);
    });
  };

  const announce = (message) => {
    let toast = document.querySelector('[data-cc-commerce-status]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cc-commerce-toast';
      toast.dataset.ccCommerceStatus = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const dotsLoaderMarkup = (label = 'Loading') => `
    <svg class="cc-dots-loader" viewBox="0 0 24 24" role="status" aria-label="${label}">
      <circle cx="4" cy="12" r="2.4"></circle>
      <circle cx="12" cy="12" r="2.4"></circle>
      <circle cx="20" cy="12" r="2.4"></circle>
    </svg>`;

  const setButtonBusy = (button, busy) => {
    if (!button) return;
    let loader = button.querySelector(':scope > .cc-inline-loader');
    if (!loader) {
      [...button.childNodes].forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) return;
        const label = document.createElement('span');
        label.className = 'cc-button-label';
        label.textContent = node.textContent;
        node.replaceWith(label);
      });
      loader = document.createElement('span');
      loader.className = 'cc-inline-loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.innerHTML = dotsLoaderMarkup();
      button.appendChild(loader);
    }
    button.classList.toggle('is-cc-loading', busy);
    button.setAttribute('aria-busy', String(busy));
    button.disabled = busy;
  };

  const normaliseLoaders = (scope = document) => {
    const selector = '.loading__spinner';
    const loaders = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...(scope.querySelectorAll?.(selector) || []),
    ];
    loaders.forEach((loader) => {
      if (loader.dataset.ccDotsLoader === 'true') return;
      loader.dataset.ccDotsLoader = 'true';
      loader.innerHTML = dotsLoaderMarkup();
    });
  };

  const enhanceSortControls = (scope = document) => {
    const selector =
      '#FacetSortDrawerForm select[name="sort_by"], #FacetSortForm select[name="sort_by"], #FacetFiltersForm .facet-filters select[name="sort_by"]';
    const selects = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...(scope.querySelectorAll?.(selector) || []),
    ];

    selects.forEach((select) => {
      if (select.dataset.ccSortEnhanced === 'true') return;
      const nativeWrap = select.closest('.select');
      const field = select.closest('.facet-filters__field');
      if (!nativeWrap || !field) return;

      const selected = select.options[select.selectedIndex] || select.options[0];
      const control = document.createElement('div');
      control.className = 'cc-sort';
      control.dataset.ccSort = '';
      control.innerHTML = `
        <button type="button" class="cc-sort__trigger" data-cc-sort-trigger aria-haspopup="listbox" aria-expanded="false">
          <span class="cc-sort__label">Sort:</span>
          <span class="cc-sort__current" data-cc-sort-current></span>
          <svg class="cc-sort__caret" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="cc-sort__panel" role="listbox" aria-label="Sort options">
          <p class="cc-sort__eyebrow">Sort by</p>
          <ul class="cc-sort__list"></ul>
        </div>`;

      control.querySelector('[data-cc-sort-current]').textContent =
        selected?.textContent?.trim() || 'Featured';
      const list = control.querySelector('.cc-sort__list');
      [...select.options].forEach((option) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const active = option.value === select.value;
        button.type = 'button';
        button.className = `cc-sort__option${active ? ' is-active' : ''}`;
        button.dataset.ccSortValue = option.value;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(active));

        const label = document.createElement('span');
        label.textContent = option.textContent.trim();
        const dot = document.createElement('span');
        dot.className = 'cc-sort__dot';
        dot.setAttribute('aria-hidden', 'true');
        button.append(label, dot);
        item.appendChild(button);
        list.appendChild(item);
      });

      select.dataset.ccSortEnhanced = 'true';
      nativeWrap.classList.add('cc-sort__native');
      field.querySelector('.facet-filters__label')?.classList.add('cc-sort__native');
      field.appendChild(control);
    });
  };

  const closeSortControls = (except = null) => {
    document.querySelectorAll('[data-cc-sort].is-open').forEach((control) => {
      if (control === except) return;
      control.classList.remove('is-open');
      control.querySelector('[data-cc-sort-trigger]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const syncHeaderGeometry = () => {
    const announcement = document.querySelector('.announcement-bar-section');
    const header = document.querySelector('.section-header');
    if (!header) return;
    const announcementHeight = Math.ceil(announcement?.getBoundingClientRect().height || 0);
    const headerHeight = Math.ceil(header.getBoundingClientRect().height || 0);
    const gap = window.matchMedia('(max-width: 749px)').matches ? 8 : 10;
    document.documentElement.style.setProperty(
      '--cc-announcement-height',
      `${announcementHeight}px`
    );
    document.documentElement.style.setProperty(
      '--cc-header-stack-height',
      `${announcementHeight + headerHeight + gap * 2}px`
    );
  };

  const recentlyViewedIds = () => {
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem('recently-viewed') || '[]');
    } catch (error) {
      list = [];
    }
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    return list
      .map((id) => String(id))
      .filter((id) => {
        if (!/^\d+$/.test(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, 8);
  };

  const recordCurrentProduct = async () => {
    if (!/^\/products\//.test(window.location.pathname)) return;
    let productId = window.ShopifyAnalytics?.meta?.product?.id;
    if (!productId) {
      try {
        const response = await nativeFetch(`${window.location.pathname}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) productId = (await response.json()).id;
      } catch (error) {
        return;
      }
    }
    const id = String(productId || '');
    if (!/^\d+$/.test(id)) return;
    try {
      const next = [id, ...recentlyViewedIds().filter((item) => item !== id)].slice(0, 10);
      localStorage.setItem('recently-viewed', JSON.stringify(next));
    } catch (error) {
      // Browsing history is an optional enhancement when storage is unavailable.
    }
  };

  const loadRecentlyViewed = async (box) => {
    if (!box) return;
    const ids = recentlyViewedIds();
    const signature = ids.join(',');
    if (box.dataset.loadedFor === signature && box.children.length) return;
    const sequence = ++recentlyViewedSequence;
    recentlyViewedAbort?.abort();
    recentlyViewedAbort =
      typeof AbortController === 'function' ? new AbortController() : null;
    box.innerHTML = '<p class="cc-cart-rv__message">Loading...</p>';
    const query = ids.length ? ids.map((id) => `id:${id}`).join(' OR ') : 'id:0';
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}search?section_id=cc-cart-recently-viewed&type=product&q=${encodeURIComponent(
      query
    )}`;

    try {
      const response = await nativeFetch(url, {
        signal: recentlyViewedAbort?.signal,
      });
      if (!response.ok) throw new Error('Unable to load recently viewed products.');
      const html = await response.text();
      if (sequence !== recentlyViewedSequence) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const results = documentFragment.querySelector('[data-cc-cart-rv-mode]');
      if (!results?.children.length) throw new Error('No recently viewed products.');
      const heading =
        results.dataset.ccCartRvMode === 'fallback'
          ? '<p class="cc-cart-rv__message">Explore our collection</p>'
          : '';
      box.innerHTML = `${heading}${results.innerHTML}`;
      box.dataset.loadedFor = signature;
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== recentlyViewedSequence) return;
      box.innerHTML = '<p class="cc-cart-rv__message">Couldn’t load these products. Select Recently viewed to try again.</p>';
      delete box.dataset.loadedFor;
    }
  };

  const initCartTabs = (scope = document) => {
    const drawers = new Set(scope.querySelectorAll?.('cart-drawer') || []);
    if (scope.matches?.('cart-drawer')) drawers.add(scope);
    const parentDrawer = scope.closest?.('cart-drawer');
    if (parentDrawer) drawers.add(parentDrawer);
    drawers.forEach((drawer) => {
      const tabCart = drawer.querySelector('#CcCartTabCart');
      const tabRecentlyViewed = drawer.querySelector('#CcCartTabRecentlyViewed');
      const paneCart = drawer.querySelector('#CcCartPaneCart');
      const paneRecentlyViewed = drawer.querySelector('#CcCartPaneRecentlyViewed');
      const box = drawer.querySelector('#CcCartRecentlyViewed');
      if (
        !tabCart ||
        !tabRecentlyViewed ||
        !paneCart ||
        !paneRecentlyViewed ||
        tabCart.dataset.ccCartTabsBound === 'true'
      ) {
        return;
      }

      tabCart.dataset.ccCartTabsBound = 'true';
      const selectTab = (name, focus = false) => {
        const toRecentlyViewed = name === 'recently-viewed';
        tabCart.setAttribute('aria-selected', String(!toRecentlyViewed));
        tabRecentlyViewed.setAttribute('aria-selected', String(toRecentlyViewed));
        tabCart.classList.toggle('is-active', !toRecentlyViewed);
        tabRecentlyViewed.classList.toggle('is-active', toRecentlyViewed);
        tabCart.tabIndex = toRecentlyViewed ? -1 : 0;
        tabRecentlyViewed.tabIndex = toRecentlyViewed ? 0 : -1;
        paneCart.hidden = toRecentlyViewed;
        paneRecentlyViewed.hidden = !toRecentlyViewed;
        if ('inert' in paneCart) paneCart.inert = toRecentlyViewed;
        if ('inert' in paneRecentlyViewed) paneRecentlyViewed.inert = !toRecentlyViewed;
        if (toRecentlyViewed) loadRecentlyViewed(box);
        if (focus) (toRecentlyViewed ? tabRecentlyViewed : tabCart).focus();
      };

      // CC-CART-TABS-0922: share the actual selection path with drawer reopen.
      drawer.selectCartTab = selectTab;
      selectTab(tabRecentlyViewed.getAttribute('aria-selected') === 'true' ? 'recently-viewed' : 'cart');
      tabCart.addEventListener('click', () => selectTab('cart'));
      tabRecentlyViewed.addEventListener('click', () => selectTab('recently-viewed'));
      [tabCart, tabRecentlyViewed].forEach((tab) => {
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const name = event.key === 'Home' ? 'cart' : event.key === 'End' ? 'recently-viewed' :
            tab === tabCart ? 'recently-viewed' : 'cart';
          selectTab(name, true);
        });
      });
    });
  };

  const sourceImageFor = (trigger) => {
    const card = trigger?.closest('[data-cc-product-card]');
    if (card) return card.querySelector('[data-cc-product-media] img, img');
    const sticky = trigger?.closest('.sticky-atc');
    if (sticky) return sticky.querySelector('.sticky-atc-image img');
    return document.querySelector(
      '.product__media-item.is-active img, .product__media-list .product__media img, .product__media img'
    );
  };

  const armFlight = (trigger, image = sourceImageFor(trigger)) => {
    if (!trigger || !image || trigger.closest('cart-drawer')) {
      pendingFlight = null;
      return;
    }

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      pendingFlight = null;
      return;
    }

    pendingFlight = {
      src: image.currentSrc || image.src,
      alt: image.alt || '',
      rect,
      createdAt: Date.now(),
    };
  };

  const cartTarget = () =>
    document.querySelector('#cart-icon-bubble, .header__icon--cart, a[href="/cart"]');

  const hitCart = (target = cartTarget()) => {
    if (!target || reducedMotion.matches || typeof target.animate !== 'function') return;

    target.__ccCartHitAnimation?.cancel();
    const bounce = target.animate(
      [
        { transform: 'scale(1)', offset: 0 },
        { transform: 'scale(1.34)', offset: 0.26 },
        { transform: 'scale(.9)', offset: 0.52 },
        { transform: 'scale(1.12)', offset: 0.76 },
        { transform: 'scale(1)', offset: 1 },
      ],
      {
        duration: 480,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
      }
    );
    target.__ccCartHitAnimation = bounce;

    const settleBounce = () => {
      if (target.__ccCartHitAnimation === bounce) target.__ccCartHitAnimation = null;
    };
    bounce.finished?.then(settleBounce).catch(settleBounce);
    window.setTimeout(() => {
      if (target.__ccCartHitAnimation !== bounce) return;
      try {
        bounce.finish();
      } catch (error) {
        bounce.cancel();
      }
      settleBounce();
    }, 620);

    const rect = target.getBoundingClientRect();
    const accent =
      getComputedStyle(document.documentElement).getPropertyValue('--cc-accent').trim() ||
      '#ff7691';
    const ring = document.createElement('span');
    ring.className = 'cc-cart-hit-ring';
    ring.setAttribute('aria-hidden', 'true');
    Object.assign(ring.style, {
      position: 'fixed',
      zIndex: '99999',
      width: '16px',
      height: '16px',
      margin: '-8px 0 0 -8px',
      borderRadius: '999px',
      border: `2px solid ${accent}`,
      pointerEvents: 'none',
      left: `${rect.left + rect.width / 2}px`,
      top: `${rect.top + rect.height / 2}px`,
    });
    document.body.appendChild(ring);

    const ringAnimation = ring.animate(
      [
        { transform: 'scale(.6)', opacity: 0.9 },
        { transform: 'scale(4.4)', opacity: 0 },
      ],
      {
        duration: 660,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
        fill: 'forwards',
      }
    );
    let ringRemoved = false;
    const removeRing = () => {
      if (ringRemoved) return;
      ringRemoved = true;
      ring.remove();
    };
    ringAnimation.finished?.then(removeRing).catch(removeRing);
    ringAnimation.onfinish = removeRing;
    window.setTimeout(removeRing, 900);
  };

  const animateFlight = async () => {
    const flight = pendingFlight;
    pendingFlight = null;
    if (!flight || reducedMotion.matches || Date.now() - flight.createdAt > 3000) return;

    const target = cartTarget();
    if (!target) return;
    const targetRect = target.getBoundingClientRect();
    if (!targetRect.width || !targetRect.height) return;

    const startSize = Math.min(96, Math.max(54, flight.rect.width));
    const ratio = flight.rect.height / flight.rect.width || 1;
    const clone = document.createElement('img');
    clone.className = 'cc-fly-item';
    clone.src = flight.src;
    clone.alt = '';
    clone.setAttribute('aria-hidden', 'true');
    Object.assign(clone.style, {
      left: `${flight.rect.left}px`,
      top: `${flight.rect.top}px`,
      width: `${startSize}px`,
      height: `${startSize * ratio}px`,
    });
    document.body.appendChild(clone);

    const dx =
      targetRect.left + targetRect.width / 2 - (flight.rect.left + startSize / 2);
    const dy =
      targetRect.top + targetRect.height / 2 - (flight.rect.top + (startSize * ratio) / 2);
    const arc = Math.max(64, Math.min(150, Math.abs(dx) * 0.16 + Math.abs(dy) * 0.08));

    try {
      const animation = clone.animate(
        [
          { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 1 },
          {
            transform: `translate3d(${dx * 0.5}px, ${dy * 0.42 - arc}px, 0) scale(.72)`,
            opacity: 0.96,
            offset: 0.52,
          },
          {
            transform: `translate3d(${dx}px, ${dy}px, 0) scale(.14)`,
            opacity: 0.12,
          },
        ],
        {
          duration: 760,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'forwards',
        }
      );
      await animation.finished;
    } catch (error) {
      // The add has already succeeded. A cancelled visual effect should never block the cart.
    } finally {
      clone.remove();
    }
  };

  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const input = args[0];
    const url = typeof input === 'string' ? input : input?.url || '';
    if (response.ok && /\/cart\/add(?:\.js)?(?:\?|$)/.test(url) && pendingFlight) {
      await animateFlight();
    }
    return response;
  };

  const addButtonSource = (event) => {
    const button = event.target.closest(
      'button[name="add"], .sticky-atc-button, [data-cc-quick-add-add]'
    );
    if (button && !button.disabled) armFlight(button);
  };

  document.addEventListener('click', addButtonSource, true);
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches('form[action*="/cart/add"], product-form form')) return;
      const button = form.querySelector('button[name="add"]');
      if (button && !button.disabled) armFlight(button);
    },
    true
  );

  const dialogMarkup = `
    <div class="cc-quick-add" data-cc-quick-add-dialog hidden>
      <button class="cc-quick-add__backdrop" type="button" data-cc-quick-add-close aria-label="Close quick add"></button>
      <section class="cc-quick-add__panel" role="dialog" aria-modal="true" aria-labelledby="CcQuickAddTitle">
        <button class="cc-quick-add__close" type="button" data-cc-quick-add-close aria-label="Close quick add">&times;</button>
        <div class="cc-quick-add__product">
          <img data-cc-quick-add-image alt="">
          <div>
            <h2 id="CcQuickAddTitle" data-cc-quick-add-title></h2>
            <div class="cc-quick-add__price" data-cc-quick-add-price></div>
          </div>
        </div>
        <div class="cc-quick-add__options" data-cc-quick-add-options></div>
        <button class="cc-quick-add__add" type="button" data-cc-quick-add-add>Add to cart</button>
        <p class="cc-quick-add__status" data-cc-quick-add-status aria-live="polite"></p>
      </section>
    </div>`;

  const ensureDialog = () => {
    let dialog = document.querySelector('[data-cc-quick-add-dialog]');
    if (!dialog) {
      document.body.insertAdjacentHTML('beforeend', dialogMarkup);
      dialog = document.querySelector('[data-cc-quick-add-dialog]');
    }
    return dialog;
  };

  const getProduct = (card) => {
    const node = card?.querySelector('[data-cc-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const fetchProduct = async (url) => {
    const cleanUrl = String(url || '').split('?')[0].replace(/\/$/, '');
    if (!cleanUrl) return null;
    if (!productCache.has(cleanUrl)) {
      productCache.set(
        cleanUrl,
        nativeFetch(`${cleanUrl}.js`, {
          headers: { Accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) throw new Error('Unable to load this product.');
          return response.json();
        })
      );
    }
    try {
      return await productCache.get(cleanUrl);
    } catch (error) {
      productCache.delete(cleanUrl);
      throw error;
    }
  };

  const productImage = (product, variant) => {
    const variantImage = variant?.featured_image?.src || variant?.featured_image;
    const featured = product?.featured_image?.src || product?.featured_image;
    return variantImage || featured || product?.images?.[0] || '';
  };

  const availableVariants = (product) =>
    (product?.variants || []).filter((variant) => variant.available);

  const exactVariant = () =>
    availableVariants(activeProduct).find((variant) =>
      (variant.options || []).every((value, index) => value === selectedOptions[index])
    );

  const canSelect = (optionIndex, value) =>
    availableVariants(activeProduct).some((variant) =>
      (variant.options || []).every(
        (variantValue, index) =>
          index === optionIndex || variantValue === selectedOptions[index]
      ) && variant.options[optionIndex] === value
    );

  const renderDialog = () => {
    const dialog = ensureDialog();
    const variant = exactVariant() || availableVariants(activeProduct)[0];
    const image = dialog.querySelector('[data-cc-quick-add-image]');
    const price = dialog.querySelector('[data-cc-quick-add-price]');
    const optionsRoot = dialog.querySelector('[data-cc-quick-add-options]');
    const add = dialog.querySelector('[data-cc-quick-add-add]');
    const optionNames = activeProduct.options || [];

    dialog.querySelector('[data-cc-quick-add-title]').textContent = activeProduct.title;
    image.src = productImage(activeProduct, variant);
    image.alt = activeProduct.title;
    price.textContent = variant ? money(variant.price) : '';
    optionsRoot.replaceChildren();

    optionNames.forEach((option, optionIndex) => {
      const name = typeof option === 'string' ? option : option.name;
      if (name === 'Title' && optionNames.length === 1) return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'cc-quick-add__group';
      const legend = document.createElement('legend');
      legend.textContent = name;
      const choices = document.createElement('div');
      choices.className = 'cc-quick-add__choices';
      const values = [
        ...new Set((activeProduct.variants || []).map((item) => item.options?.[optionIndex])),
      ].filter(Boolean);

      values.forEach((value) => {
        const choice = document.createElement('button');
        choice.type = 'button';
        choice.className = 'cc-quick-add__choice';
        choice.textContent = value;
        choice.setAttribute('aria-pressed', String(selectedOptions[optionIndex] === value));
        choice.disabled = !canSelect(optionIndex, value);
        choice.addEventListener('click', () => {
          selectedOptions[optionIndex] = value;
          renderDialog();
        });
        choices.appendChild(choice);
      });

      fieldset.append(legend, choices);
      optionsRoot.appendChild(fieldset);
    });

    add.disabled = !variant;
    add.dataset.variantId = variant?.id || '';
  };

  const closeDialog = (restoreFocus = true) => {
    const dialog = ensureDialog();
    const returnFocus = activeTrigger;
    const delay = reducedMotion.matches ? 0 : 310;
    dialog.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    return new Promise((resolve) => {
      window.setTimeout(() => {
        dialog.hidden = true;
        if (restoreFocus && returnFocus) returnFocus.focus();
        activeTrigger = null;
        activeProduct = null;
        activeCard = null;
        resolve();
      }, delay);
    });
  };

  const openDialog = (trigger, card, product) => {
    const first = availableVariants(product)[0];
    if (!first) return;
    activeTrigger = trigger;
    activeCard = card;
    activeProduct = product;
    selectedOptions = [...(first.options || [])];
    const dialog = ensureDialog();
    dialog.hidden = false;
    renderDialog();
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      dialog.classList.add('is-open');
      dialog.querySelector('[data-cc-quick-add-close]:not(.cc-quick-add__backdrop)').focus();
    });
  };

  const addVariant = async (variantId, trigger, card, direct = false) => {
    const dialog = ensureDialog();
    const add = dialog.querySelector('[data-cc-quick-add-add]');
    const status = dialog.querySelector('[data-cc-quick-add-status]');
    const busyButton = direct ? trigger : add;
    const returnFocus = activeTrigger || trigger;
    setButtonBusy(busyButton, true);
    if (!direct) status.textContent = '';
    armFlight(trigger, card?.querySelector('[data-cc-product-media] img, img'));
    if (!direct && !dialog.hidden) await closeDialog(false);

    const drawer = document.querySelector('cart-drawer');
    const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [
      'cart-drawer',
      'cart-icon-bubble',
    ];

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Unable to add this item.');

      setButtonBusy(busyButton, false);
      if (drawer && state.sections) {
        drawer.setActiveElement(returnFocus);
        drawer.renderContents(state);
        hitCart(cartTarget());
      } else {
        window.location.assign('/cart');
      }
    } catch (error) {
      if (direct || dialog.hidden) {
        announce(error.message || 'Unable to add this item. Please try again.');
      } else {
        status.textContent = error.message || 'Unable to add this item. Please try again.';
      }
      setButtonBusy(busyButton, false);
    }
  };

  const addRecentlyViewedVariant = async (button) => {
    const variantId = Number(button.dataset.ccCartRvAdd);
    if (!variantId || button.disabled) return;
    setButtonBusy(button, true);
    const drawer = button.closest('cart-drawer');
    const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [
      'cart-drawer',
      'cart-icon-bubble',
    ];

    try {
      const response = await nativeFetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || 'Unable to add this item.');
      setButtonBusy(button, false);
      if (drawer && state.sections) {
        drawer.renderContents(state);
      } else {
        window.location.assign('/cart');
      }
      announce('Added to cart.');
    } catch (error) {
      announce(error.message || 'Unable to add this item. Please try again.');
      if (button.isConnected) {
        setButtonBusy(button, false);
      }
    }
  };

  document.addEventListener('click', async (event) => {
    const sortTrigger = event.target.closest('[data-cc-sort-trigger]');
    if (sortTrigger) {
      const control = sortTrigger.closest('[data-cc-sort]');
      const opening = !control.classList.contains('is-open');
      closeSortControls(control);
      control.classList.toggle('is-open', opening);
      sortTrigger.setAttribute('aria-expanded', String(opening));
      return;
    }

    const sortOption = event.target.closest('[data-cc-sort-value]');
    if (sortOption) {
      const control = sortOption.closest('[data-cc-sort]');
      const select = control?.closest('form')?.querySelector('select[name="sort_by"]');
      if (!select || select.value === sortOption.dataset.ccSortValue) {
        closeSortControls();
        return;
      }

      select.value = sortOption.dataset.ccSortValue;
      control.querySelector('[data-cc-sort-current]').textContent =
        sortOption.querySelector('span')?.textContent?.trim() || '';
      control.querySelectorAll('[data-cc-sort-value]').forEach((button) => {
        const active = button === sortOption;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      closeSortControls();
      select.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    if (!event.target.closest('[data-cc-sort]')) closeSortControls();

    const menuClose = event.target.closest('[data-cc-menu-close]');
    if (menuClose) {
      event.preventDefault();
      menuClose
        .closest('#Details-menu-drawer-container')
        ?.querySelector(':scope > summary')
        ?.click();
      return;
    }

    const recentlyViewedAdd = event.target.closest('[data-cc-cart-rv-add]');
    if (recentlyViewedAdd) {
      event.preventDefault();
      addRecentlyViewedVariant(recentlyViewedAdd);
      return;
    }

    const close = event.target.closest('[data-cc-quick-add-close]');
    if (close) {
      closeDialog();
      return;
    }

    const quickAdd = event.target.closest('[data-cc-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      const card = quickAdd.closest('[data-cc-product-card]');
      let product = getProduct(card);
      if (!product && quickAdd.dataset.productUrl) {
        setButtonBusy(quickAdd, true);
        try {
          product = await fetchProduct(quickAdd.dataset.productUrl);
        } catch (error) {
          announce(error.message || 'Unable to load this product. Please try again.');
        } finally {
          if (quickAdd.isConnected) setButtonBusy(quickAdd, false);
        }
      }
      if (!product) return;
      const variants = availableVariants(product);
      if (variants.length === 1 && (product.options || []).length <= 1) {
        activeTrigger = quickAdd;
        activeCard = card;
        activeProduct = product;
        addVariant(variants[0].id, quickAdd, card, true);
      } else {
        openDialog(quickAdd, card, product);
      }
      return;
    }

    const add = event.target.closest('[data-cc-quick-add-add]');
    if (add && add.dataset.variantId) {
      addVariant(add.dataset.variantId, add, activeCard);
    }
  });

  const handleNativeQuickAddClick = async (event) => {
    const button = event.currentTarget;
    if (!button?.matches?.('.quick-add__submit[data-product-url]')) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const card = button.closest('.card-wrapper');
    setButtonBusy(button, true);
    try {
      const product = await fetchProduct(button.dataset.productUrl);
      const variants = availableVariants(product);
      if (!variants.length) throw new Error('This product is currently unavailable.');
      if (variants.length === 1 && (product.options || []).length <= 1) {
        activeTrigger = button;
        activeCard = card;
        activeProduct = product;
        await addVariant(variants[0].id, button, card, true);
      } else {
        openDialog(button, card, product);
      }
    } catch (error) {
      announce(error.message || 'Unable to load this product. Please try again.');
    } finally {
      if (button.isConnected) {
        setButtonBusy(button, false);
      }
    }
  };

  const createMissingQuickAdds = (scope = document) => {
    const selector = '.product-grid .card-wrapper';
    const cards = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...(scope.querySelectorAll?.(selector) || []),
    ];
    cards.forEach((card) => {
      if (card.querySelector('.quick-add__submit[data-product-url]')) return;
      const media = card.querySelector('.card__inner');
      const productLink = card.querySelector(
        '.card__heading a[href*="/products/"], a.full-unstyled-link[href*="/products/"]'
      );
      if (!media || !productLink) return;
      const productUrl = new URL(productLink.href, window.location.origin).pathname;
      const title = productLink.textContent.trim() || 'product';
      const quickAdd = document.createElement('div');
      quickAdd.className = 'quick-add no-js-hidden';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'quick-add__submit button button--full-width button--secondary';
      button.dataset.productUrl = productUrl;
      button.setAttribute('aria-label', `Quick add ${title}`);
      button.innerHTML = '<span>+ Quick add</span>';
      quickAdd.appendChild(button);
      media.appendChild(quickAdd);
    });
  };

  const prepareNativeQuickAdds = (scope = document) => {
    const selector = '.quick-add__submit[data-product-url]';
    const buttons = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...scope.querySelectorAll(selector),
    ];
    buttons.forEach((button) => {
      if (button.dataset.ccNativeQuickAdd === 'true') return;
      button.dataset.ccNativeQuickAdd = 'true';
      button.type = 'button';
      button.removeAttribute('aria-labelledby');
      const card = button.closest('.card-wrapper');
      const title =
        card?.querySelector('.card__heading, .card-information__text, [class*="card__heading"]')
          ?.textContent?.trim() || 'product';
      button.setAttribute('aria-label', `Quick add ${title}`);
      const label =
        button.querySelector('span:not(.loading__spinner)') ||
        [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
      if (label) label.textContent = '+ Quick add';

      const quickAdd = button.closest('.quick-add');
      const media = card?.querySelector('.card__inner');
      if (quickAdd && media) {
        quickAdd.classList.add('cc-card-quick-add');
        if (quickAdd.parentElement !== media) media.appendChild(quickAdd);
      }
      button.addEventListener('click', handleNativeQuickAddClick, true);
    });
  };

  const prepareQuickAdds = (scope = document) => {
    createMissingQuickAdds(scope);
    prepareNativeQuickAdds(scope);
  };

  const setUgcVideoState = (card, shouldPlay) => {
    const video = card?.querySelector('.cc-ugc-video__media');
    const button = card?.querySelector('[data-cc-ugc-video-toggle]');
    const icon = button?.querySelector('[data-cc-ugc-video-icon]');
    if (!video || !button) return;

    if (shouldPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }

    button.setAttribute(
      'aria-label',
      shouldPlay ? button.dataset.pauseLabel : button.dataset.playLabel
    );
    button.dataset.state = shouldPlay ? 'playing' : 'paused';
    if (icon && !icon.querySelector('svg')) icon.textContent = shouldPlay ? 'Ⅱ' : '▶';
  };

  const ugcVideoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const card = entry.target;
              const shouldPlay = entry.isIntersecting && !card.dataset.ccUserPaused;
              setUgcVideoState(card, shouldPlay);
            });
          },
          { threshold: 0.35 }
        )
      : null;

  const prepareUgcVideos = (scope = document) => {
    const selector = '[data-cc-ugc-video]';
    const cards = [
      ...(scope.matches?.(selector) ? [scope] : []),
      ...scope.querySelectorAll(selector),
    ];

    cards.forEach((card) => {
      if (card.dataset.ccUgcReady === 'true') return;
      card.dataset.ccUgcReady = 'true';
      const button = card.querySelector('[data-cc-ugc-video-toggle]');
      const video = card.querySelector('.cc-ugc-video__media');
      if (!button || !video) return;

      button.addEventListener('click', () => {
        if (video.paused) {
          delete card.dataset.ccUserPaused;
          setUgcVideoState(card, true);
        } else {
          card.dataset.ccUserPaused = 'true';
          setUgcVideoState(card, false);
        }
      });

      if (ugcVideoObserver) {
        ugcVideoObserver.observe(card);
      } else {
        setUgcVideoState(card, true);
      }
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('[data-cc-sort].is-open')) {
      const trigger = document.querySelector(
        '[data-cc-sort].is-open [data-cc-sort-trigger]'
      );
      closeSortControls();
      trigger?.focus();
      return;
    }

    const dialog = document.querySelector('[data-cc-quick-add-dialog].is-open');
    if (!dialog) return;
    if (event.key === 'Escape') {
      closeDialog();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [
      ...dialog.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])'
      ),
    ];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* bindConceptFill removed: the sticky ATC sweep is owned by cc-sweep.js
     (Concept-exact one-way conveyor) — two drivers must never share a button. */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        prepareQuickAdds();
        prepareUgcVideos();
        normaliseLoaders();
        enhanceSortControls();
        initCartTabs();
        bindCheckoutAttention();
        updateShippingProgress();
        syncHeaderGeometry();
        recordCurrentProduct();
      },
      { once: true }
    );
  } else {
    prepareQuickAdds();
    prepareUgcVideos();
    normaliseLoaders();
    enhanceSortControls();
    initCartTabs();
    bindCheckoutAttention();
    updateShippingProgress();
    syncHeaderGeometry();
    recordCurrentProduct();
  }
  window.addEventListener('load', syncHeaderGeometry, { once: true });
  window.addEventListener('resize', syncHeaderGeometry, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    prepareQuickAdds(event.target);
    prepareUgcVideos(event.target);
    normaliseLoaders(event.target);
    enhanceSortControls(event.target);
    initCartTabs(event.target);
    bindCheckoutAttention(event.target);
    updateShippingProgress(event.target);
    requestAnimationFrame(syncHeaderGeometry);
  });
  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        prepareQuickAdds(node);
        prepareUgcVideos(node);
        normaliseLoaders(node);
        enhanceSortControls(node);
        initCartTabs(node);
        bindCheckoutAttention(node);
        updateShippingProgress(node);
      })
    );
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
